import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { BookingMockup } from '../components/visuals/BookingMockup.js';
import { IconClinic } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-financial-planners')({
  component: FinancialPlannersPage,
});

function FinancialPlannersPage() {
  usePageTitle('Bookings for financial planners');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For financial planners',
  icon: IconClinic,
  headline: 'Annual reviews that never slip the year.',
  headlineEmphasis: 'never slip',
  subhead:
    'Bookaburra tracks when each client is due for their annual review or quarterly check-in, and lets them re-book themselves from the reminder. You stop chasing — they self-serve.',
  Visual: BookingMockup,
  business: {
    name: 'Bayside Wealth Advisers',
    location: 'Melbourne bayside · 2 planners · 1 boardroom + 1 office',
    summary:
      'A fictional 2-planner advisory practice in bayside Melbourne. Mix of 90-minute statement-of-advice meetings (boardroom), 60-minute annual reviews, and 30-minute quarterly check-ins. Compliance docs and prep eat into every meeting; under-running is worse than over-running here.',
  },
  dayTimeline: [
    {
      time: '9:00',
      event:
        'Day opens. Boardroom is held for an 11am statement-of-advice; the other office is running back-to-back quarterly check-ins.',
      bookaburraResponse:
        'Nothing to do. Each meeting was held against the room its service required.',
    },
    {
      time: '9:30',
      event:
        'Client opens yesterday’s 24-hour reminder for tomorrow’s annual review. Prep doc was attached.',
      bookaburraResponse:
        'Reminders went out the day before, not the morning of. Prep starts when it should.',
    },
    {
      time: '10:45',
      event:
        'A new prospect opens the picker for a 90-minute statement-of-advice. There’s a 45-minute gap between reviews mid-morning.',
      bookaburraResponse:
        'That gap is never shown — the picker only offers times with a full 90 minutes free in the boardroom. Earliest viable slot is Thursday 10:00.',
    },
    {
      time: '12:00',
      event:
        'Mrs Henderson (annual review well overdue) hasn’t been chased in months.',
      bookaburraResponse:
        'She’s on today’s follow-up list. One magic-link "book your review" email goes out; she self-books for next Thursday.',
    },
    {
      time: '14:20',
      event:
        'A quarterly check-in runs short — boardroom is free again from 14:40.',
      bookaburraResponse:
        'Nothing automatic, but the next 30-minute check-in opens up online for any client clicking through their reminder.',
    },
    {
      time: '15:30',
      event:
        'Existing client opens his FDS reminder and clicks reschedule on the review slot.',
      bookaburraResponse:
        'Magic-link reschedule offers the next 60-minute slots with the same planner. Client picks a new time — calendar feed updates without staff intervention.',
    },
  ],
  pains: [
    {
      quote:
        "“I’ll book Mrs Henderson next week” turns into three months later, and her annual review is now well past due.",
      explanation:
        'Annual reviews drift if nobody is watching the calendar. The system should know who is overdue.',
    },
    {
      quote:
        'A 60-minute annual review got booked into a 30-minute gap. I had to ring the client back and reschedule.',
      explanation:
        'A start time is not enough. The slot has to fit the full meeting, or the apology call is on you.',
    },
    {
      quote:
        'I forgot a statement-of-advice meeting was on Tuesday until Monday afternoon. Prep should start the day before, not the day of.',
      explanation:
        'High-prep meetings need a heads-up the day before, every time.',
    },
  ],
  solutions: [
    {
      title: 'See who is overdue for a review',
      body: (
        <>
          A follow-up list shows clients whose annual review or quarterly
          check-in is overdue, based on each service&rsquo;s recommended frequency.
          You stop chasing — the list does it for you.
        </>
      ),
    },
    {
      title: 'Stop squeezing long meetings',
      body: (
        <>
          A 90-minute statement-of-advice meeting cannot be squeezed into a
          30-minute gap. Slots respect the service duration so the meeting
          actually fits.
        </>
      ),
    },
    {
      title: 'Reminders the day before, not the day of',
      body: (
        <>
          24-hour reminders go out automatically so prep starts the day before
          the meeting, not the morning of.
        </>
      ),
    },
    {
      title: 'Clients reschedule themselves',
      body: (
        <>
          Every reminder includes a magic-link to reschedule. When life happens,
          the client moves their own slot — no phone tag, no email chain.
        </>
      ),
    },
  ],
  industrySlug: 'financial-planners',
};
